import { useQuery, useQueryClient } from '@tanstack/react-query';

import { useToaster } from '@components/toaster';
import { useThrottledMutation as useMutation } from '@hooks/use-throttled-mutation';
import { useScopedTranslate } from '@locale/translate';
import { toQueryString } from '@utils/requests';

import {
  AttributeTemplateData,
  TravellerAttributeTemplateData,
  createAttributeTemplate,
  createTravellerAttributeTemplate,
  deleteAttributeTemplate,
  deleteTravellerAttributeTemplate,
  duplicateAttributeTemplate,
  duplicateTravellerAttributeTemplate,
  loadAttributeTemplates,
  loadCompanyTravellerAttributeTemplate,
  loadCompanyTravellerAttributeTemplateVariables,
  loadCompanyTravellerAttributeTemplates,
  updateAttributeTemplate,
  updateTravellerAttributeTemplate,
} from './requests';

export const useAttributeTemplates = (queryData?: object) => {
  const queryString = toQueryString(queryData ?? {});
  return useQuery({
    keepPreviousData: true,
    queryKey: ['journey-attributes', queryString],
    queryFn: () => loadAttributeTemplates(queryString),
  });
};

export const useCreateAttributeTemplate = () => {
  const { t } = useScopedTranslate(locale);
  const { showSuccess } = useToaster();

  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createAttributeTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries(['journey-attributes']);
      showSuccess(t('attributeTemplateCreated'));
    },
  });
};

export const useUpdateAttributeTemplate = () => {
  const { t } = useScopedTranslate(locale);
  const { showSuccess } = useToaster();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ data, shortId }: { data: AttributeTemplateData; shortId: string }) =>
      updateAttributeTemplate(shortId, data),
    onSuccess: () => {
      queryClient.invalidateQueries(['journey-attributes']);
      showSuccess(t('attributeEdited'));
    },
  });
};

export const useDeleteAttributeTemplate = () => {
  const { t } = useScopedTranslate(locale);
  const { showSuccess } = useToaster();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (shortId: string) => deleteAttributeTemplate(shortId),
    onSuccess: () => {
      queryClient.invalidateQueries(['journey-attributes']);
      showSuccess(t('attributeDeleted'));
    },
  });
};

export const useDuplicateAttributeTemplate = () => {
  const { t } = useScopedTranslate(locale);
  const { showSuccess } = useToaster();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (shortId: string) => duplicateAttributeTemplate(shortId),
    onSuccess: () => {
      queryClient.invalidateQueries(['journey-attributes']);
      showSuccess(t('attributeTemplateDuplicated'));
    },
  });
};

export const useCompanyTravellerAttributeTemplates = ({
  queryData,
  systemAttributes,
  ...rest
}: {
  queryData?: object;
  systemAttributes?: boolean;
  staleTime?: number;
  keepPreviousData?: boolean;
} = {}) => {
  const queryString = toQueryString(queryData ?? {});
  return useQuery({
    keepPreviousData: true,
    staleTime: 1000,
    queryKey: ['traveller-attributes', queryString, systemAttributes],
    queryFn: () => loadCompanyTravellerAttributeTemplates(queryString, systemAttributes),
    ...rest,
  });
};

export const useCompanyTravellerAttributeTemplate = (
  shortId: string,
  isSystemAttributeQuery?: boolean
) => {
  return useQuery({
    queryKey: ['traveller-attributes', shortId],
    queryFn: () => loadCompanyTravellerAttributeTemplate(shortId, isSystemAttributeQuery),
    enabled: Boolean(shortId),
  });
};

export const useCompanyTravellerAttributeTemplateVariables = () => {
  return useQuery({
    queryKey: ['traveller-attribute-variables'],
    queryFn: () => loadCompanyTravellerAttributeTemplateVariables(),
  });
};

export const useCreateTravellerAttributeTemplate = () => {
  const { t } = useScopedTranslate(locale);
  const { showSuccess } = useToaster();

  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: createTravellerAttributeTemplate,
    onSuccess: () => {
      queryClient.invalidateQueries(['traveller-attributes']);
      showSuccess(t('attributeTemplateCreated'));
    },
  });
};

export const useUpdateTravellerAttributeTemplate = () => {
  const { t } = useScopedTranslate(locale);
  const { showSuccess } = useToaster();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ data, shortId }: { data: TravellerAttributeTemplateData; shortId: string }) =>
      updateTravellerAttributeTemplate(shortId, data),
    onSuccess: ({ shortId }) => {
      queryClient.removeQueries(['traveller-attributes', shortId]);
      queryClient.invalidateQueries(['traveller-attributes']);
      showSuccess(t('attributeEdited'));
    },
  });
};

export const useDuplicateTravellerAttributeTemplate = () => {
  const { t } = useScopedTranslate(locale);
  const { showSuccess } = useToaster();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (shortId: string) => duplicateTravellerAttributeTemplate(shortId),
    onSuccess: () => {
      queryClient.invalidateQueries(['traveller-attributes']);
      showSuccess(t('attributeTemplateDuplicated'));
    },
  });
};

export const useDeleteTravellerAttributeTemplate = () => {
  const { t } = useScopedTranslate(locale);
  const { showSuccess } = useToaster();
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (shortId: string) => deleteTravellerAttributeTemplate(shortId),
    onSuccess: () => {
      queryClient.invalidateQueries(['traveller-attributes']);
      showSuccess(t('attributeDeleted'));
    },
  });
};

export const locale = {
  en: {
    attributeTemplateCreated: 'Field created',
    attributeTemplateDuplicated: 'Field duplicated',
    attributeEdited: 'Field edited',
    attributeDeleted: 'Field deleted',
  },
};
